import Card from '@/components/Card/Card';
import Header from '@/components/Header/Header';
import Table from '@/components/Tables/Table/Table';
import Tag from '@/components/Tag/Tag';
import { TagTypes } from '@/components/Tag/Tag.types';
import { ToastTypes } from '@/components/Toast/Toast.types';
import Tooltip from '@/components/Tooltip/Tooltip';
import Button from '@/components/Button/Button';
import { PositionTypes } from '@/components/Tooltip/Tooltip.types';
import { ButtonVariants } from '@/components/Button/Button.types';
import Placeholder from '@/images/BundleDetailsPlaceholder.svg';
import { useYesNoTranslation } from '@/pages/Franchisee/Bundles/Active/internal/UseYesNoTranslation';
import { usePromotion, usePromotionsDeactivate } from '@/services/promotions/promotions.service';
import {
  DeactivatePromotionPayload,
  Effect,
  EffectPricingTypeNameEnum,
  EffectTypeNameEnum,
  PricingTypeEnum,
} from '@/types/promotions.types';
import { renderOccasions } from '@/utils/formatting/formatBundleProperties';
import useNavigateWithToast from '@/utils/useNavigateWithToast/useNavigateWithToast';
import { Children, FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styles from './PromotionDetails.module.css';
import PromotionSettings from './PromotionSettings';
import {
  formatPromotionAmountRange,
  formatPromotionPercentRange,
  getColumnDefinition,
  isMissingNumber,
  toFixedAmount,
} from '@/services/promotions/promotions.helper.service';
import DeactivatePromosModal from '../components/DeactivatePromosModal/DeactivatePromosModal';
import { showToast } from '@/components/Toast/Toast';
import { useExistsActiveGlobalFreezes } from '@/services/freeze/freeze.service';

const Promotion: FC = () => {
  const { t } = useTranslation();
  const { promotionId } = useParams();
  const { data: existsActiveGlobalFreeze } = useExistsActiveGlobalFreezes();
  const navigateWithToast = useNavigateWithToast();
  const { yesNoTranslation } = useYesNoTranslation();
  const [showConfirmedModal, setShowConfirmedModal] = useState(false);
  const { data, isLoading, isError } = usePromotion(promotionId !== undefined, promotionId!);
  const promotionDetails = data?.data;
  const {
    mutate: deactivatePromotions,
    isLoading: isDeactivatePromotionsLoading,
    reset: deactivatePromotionsReset,
  } = usePromotionsDeactivate(promotionId!);
  const isLock = promotionDetails?.isLock || false;
  const isVisible = !!promotionDetails?.isVisible;
  const showDeactivatePromoInfo = promotionDetails !== undefined && promotionDetails.isVisible === false && !isLock;
  const isCER = promotionDetails?.description === 'CER';
  const noActiveGlobalFreeze = !existsActiveGlobalFreeze;
  const hasOnlyInactiveStoreGroups =
    (promotionDetails?.promotionStatusGroup?.length ?? 0) > 0 &&
    promotionDetails?.promotionStatusGroup?.every((item) => item.status === 'Inactive');

  const promotionLoaded = !isLoading && promotionDetails !== undefined;

  if (isError) {
    navigateWithToast('/', {
      label: 'Error fetching promotion.',
      toastType: ToastTypes.Error,
      id: 'errorFetchingPromotion',
    });
  }

  function priceOrDiscountValue(effect: Effect) {
    switch (effect.pricingTypeName) {
      case PricingTypeEnum.PERCENT_OFF_PROMOTION:
        return isMissingNumber(effect.percentage) ? 'N/A' : `${effect.percentage}%`;
      case PricingTypeEnum.AMOUNT_OFF_PROMOTION: {
        const amount = toFixedAmount(effect.amount);
        return amount === null ? 'N/A' : `$${amount}`;
      }
      case PricingTypeEnum.FIXED_PRICE_PROMOTION: {
        const amount = toFixedAmount(effect.amount);
        return amount === null ? 'N/A' : `$${amount}`;
      }
      case PricingTypeEnum.VARIABLE_PERCENT_OFF_PROMOTION: {
        const range = formatPromotionPercentRange(effect.minimumPercentage, effect.maximumPercentage);
        return typeof range === 'string' ? range : `${range.percentMin} - ${range.percentMax}%`;
      }
      case PricingTypeEnum.VARIABLE_AMOUNT_OFF_PROMOTION: {
        const range = formatPromotionAmountRange(effect.minimumAmount, effect.maximumAmount);
        return typeof range === 'string' ? range : `$${range.amountMin} - $${range.amountMax}`;
      }
      case PricingTypeEnum.VARIABLE_FIXED_PRICE_PROMOTION: {
        const range = formatPromotionAmountRange(effect.minimumFixedPrice, effect.maximumFixedPrice);
        return typeof range === 'string' ? range : `$${range.amountMin} - $${range.amountMax}`;
      }
      default:
        return null;
    }
  }

  const promoEffectsData = promotionDetails?.effects?.map((effect: Effect) => ({
    effectType: EffectTypeNameEnum[effect.typeName as keyof typeof EffectTypeNameEnum],
    pricingEffectType: EffectPricingTypeNameEnum[effect.pricingTypeName as keyof typeof EffectPricingTypeNameEnum],
    priceOrDiscount: priceOrDiscountValue(effect),
    options: effect.options,
  }));

  const getNoOfActiveStoreGroups = () =>
    promotionDetails?.promotionStatusGroup?.filter((item) => item.status === 'Active').length;

  const getActivePillLabel = () =>
    `(${getNoOfActiveStoreGroups()} ${
      getNoOfActiveStoreGroups()! > 1 ? t('common.storeGroups') : t('common.storeGroup')
    })`;

  const occasion = renderOccasions(t, promotionDetails?.occasions);
  const editToggles = !!(promotionDetails?.isVisible && noActiveGlobalFreeze);

  const columnDefinitions = useMemo(() => {
    const showUpchargeColumn =
      promotionDetails?.effects?.filter((effect) => effect.pricingTypeName === 'FixedPricePromotion') || [];
    return getColumnDefinition(t, showUpchargeColumn.length > 0);
  }, [promotionDetails?.effects, t]);

  const handleDeactivateConfirmModal = async () => {
    const storeGroupsIds = promotionDetails?.promotionStatusGroup?.map((item) => item.groupId);
    const payload: DeactivatePromotionPayload = {
      promotionIds: [promotionDetails?.promotionId ?? ''],
      addStoreGroupsIds: [],
      removeStoreGroupsIds: storeGroupsIds ?? [],
    };
    deactivatePromotions(payload, {
      onSuccess: () => {
        showToast({
          label: t('promotions.promotionDetailsToast.success'),
          id: 'deactivatePromotionsSuccess',
        });
        setShowConfirmedModal(false);
      },
      onError: () => {
        showToast({
          label: t('promotions.promotionDetailsToast.failed'),
          toastType: ToastTypes.Error,
          id: 'errorDeactivatePromotions',
        });
        setShowConfirmedModal(false);
        deactivatePromotionsReset();
      },
    });
  };

  return (
    <div className={styles.page}>
      <Header
        title={promotionDetails?.displayName ?? t('promotions.promotionDetails')}
        breadcrumb={{ label: t('breadcrumb.backToPromotions'), to: '/promotions/active' }}
      />
      <div className={styles.topContainer}>
        <div className={styles.titleRow}>
          <div className={styles.titleGroup}>
            <h2 className={styles.title}>{t('promotions.promotionDetails')}</h2>
            <Tag
              type={getNoOfActiveStoreGroups() ? TagTypes.Completed : TagTypes.Inactive}
              label={
                getNoOfActiveStoreGroups() ? (
                  <div>
                    <span>{`${t('common.active')} `}</span>
                    <span className={styles.activeCountForPill}>{getActivePillLabel()}</span>
                  </div>
                ) : (
                  t('common.inactive')
                )
              }
            />
          </div>

          {promotionLoaded && !isLock && noActiveGlobalFreeze && !hasOnlyInactiveStoreGroups && (
            <Button
              isLoading={isDeactivatePromotionsLoading}
              variant={ButtonVariants.Secondary}
              onClick={() => setShowConfirmedModal(true)}
            >
              {t('common.deactivate')}
            </Button>
          )}
        </div>
        <Card>
          <div className={styles.row}>
            <div>
              <img className={styles.image} src={Placeholder} alt="Promotion Placeholder" />
            </div>
            <div className={styles.row}>
              <div className={styles.columnContainerAndRestriction}>
                <div className={styles.promotionDetailsContainer}>
                  <div className={styles.column}>
                    <div className={styles.promotionDetails}>
                      <div className={styles.label}>{t('promotions.table.name')}: </div>
                      <span>{promotionDetails?.name || '-'}</span>
                    </div>
                    <div className={styles.promotionDetails}>
                      <div className={styles.label}>{t('promotions.table.displayName')}: </div>
                      <span>{promotionDetails?.displayName || '-'}</span>
                    </div>
                    <div className={styles.promotionDetails}>
                      <div className={styles.label}>{t('promotions.table.description')}: </div>
                      <span>{promotionDetails?.description || '-'}</span>
                    </div>
                    <div className={styles.promotionDetails}>
                      <div className={styles.label}>{t('promotions.table.occasion')}: </div>
                      <div>
                        <span key={occasion}>{occasion}</span>
                      </div>
                    </div>
                    <div className={styles.promotionDetails}>
                      <div className={styles.label}>{t('promotions.code')}: </div>
                      <div>
                        {!promotionDetails?.redemptionCodes?.length ? (
                          <span>-</span>
                        ) : (
                          Children.toArray(
                            promotionDetails.redemptionCodes.map((code, index) => (
                              <>
                                {index > 0 && ', '}
                                <span key={code}>{code}</span>
                              </>
                            )),
                          )
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.column}>
                    <div className={styles.secondPromotionDetails}>
                      <div className={styles.label}>{t('promotions.activeStores')}: </div>
                      <span>{promotionDetails?.activeStoreCount || '-'}</span>
                    </div>
                    <div className={styles.secondPromotionDetails}>
                      <div className={styles.label}>{t('promotions.combinable')}: </div>
                      <span>{yesNoTranslation(promotionDetails?.isCombinable) || '-'}</span>
                    </div>
                    <div className={styles.secondPromotionDetails}>
                      <div className={styles.label}>{t('promotions.maxUses')}: </div>
                      <span>{promotionDetails?.maxUses || '-'}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.columnRestriction}>
                  <div className={styles.promotionRestrictionsText}>
                    <strong>{t('promotions.restrictions')}:</strong>
                    {promotionDetails?.legalText || '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.restrictionsRow}>
            <div className={styles.promotionRestrictionsTextRow}>
              <strong>{t('promotions.restrictions')}:</strong>
              {promotionDetails?.legalText || '-'}
            </div>
          </div>

          <div className={styles.tableContainer}>
            <div className={styles.headerWithTooltip}>
              <h3 className={styles.heading}>Effects</h3>
              <Tooltip
                content="Effects indicate what change will be made to customer price when the promotion is applied. There can be multiple effects on one promotion, especially when that promotion targets a specific product or product count."
                position={PositionTypes.Top}
              />
            </div>
            <Table
              width="calc(100vw - 372px)"
              className={styles.overflowVisible}
              isLoading={isLoading}
              loadingRowCount={3}
              data={promoEffectsData || []}
              columns={columnDefinitions}
            />
          </div>
        </Card>
      </div>
      <PromotionSettings
        existsActiveGlobalFreeze={existsActiveGlobalFreeze}
        originalData={promotionDetails?.promotionStatusGroup || []}
        promotionId={promotionId}
        editToggles={editToggles}
        isLock={isLock}
        isCER={isCER}
        isVisible={isVisible}
        showDeactivatePromoInfo={showDeactivatePromoInfo}
      />

      <DeactivatePromosModal
        isOpen={showConfirmedModal}
        onClose={() => setShowConfirmedModal(false)}
        onConfirm={handleDeactivateConfirmModal}
        isLoading={isDeactivatePromotionsLoading}
      />
    </div>
  );
};

export default Promotion;
